import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Spin } from 'antd';
import { EnvConfig, loadRuntimeConfig, validateConfig } from '@/config/env';
import { ConfigError } from '../components/ConfigError/ConfigError';

interface ConfigContextType {
  config: EnvConfig;
  isLoaded: boolean;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

/**
 * ConfigProvider loads runtime configuration before rendering children
 * Shows loading spinner while loading, error screen if config is invalid
 */
export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<EnvConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  useEffect(() => {
    loadRuntimeConfig()
      .then((loadedConfig) => {
        setConfig(loadedConfig);
        const missing = validateConfig();
        setMissingFields(missing);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load configuration:', error);
        setMissingFields(['ADMIN_API_URL']);
        setLoading(false);
      });
  }, []);

  // Show loading spinner while loading config
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <Spin size="large" tip="Loading configuration..." />
      </div>
    );
  }

  // Show error screen if required config is missing
  if (missingFields.length > 0) {
    return <ConfigError missingFields={missingFields} />;
  }

  // Config is loaded and valid
  return (
    <ConfigContext.Provider value={{ config: config!, isLoaded: true }}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * Hook to access configuration
 */
export function useConfig(): EnvConfig {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context.config;
}

export default ConfigProvider;

